import type { Key, Node, Selection } from "@react-types/shared";
import React, { type ReactNode, useMemo, useRef } from "react";
import {
    mergeProps,
    useCheckbox,
    useFocusRing,
    useGridList,
    useGridListItem,
    useGridListSelectionCheckbox,
} from "react-aria";
import { Item, useListState, useToggleState } from "react-stately";

import type { DataTableDensity } from "@/components/flexkit/tables/DataTablePrimitives";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";

// ============================================================================
// MobileRowDescriptor — structured content for Tier 2
// ============================================================================

export type MobileRowDetail = {
    label?: string;
    value: ReactNode;
};

export type MobileRowDescriptor = {
    /** Primary content — rendered bold, first line */
    title: ReactNode;
    /** Optional secondary line below title */
    description?: ReactNode;
    /** Optional labeled detail fields, rendered in a balanced CSS grid */
    details?: MobileRowDetail[];
    /** Optional icon/avatar — rendered left of title */
    icon?: ReactNode;
};

// ============================================================================
// Type guard: descriptor vs raw JSX
// ============================================================================

function isMobileRowDescriptor(value: unknown): value is MobileRowDescriptor {
    return (
        typeof value === "object" &&
        value !== null &&
        "title" in value &&
        !React.isValidElement(value) &&
        !Array.isArray(value)
    );
}

// ============================================================================
// DataTableMobileView
// ============================================================================

type DataTableMobileViewProps<T> = {
    data: T[];
    getRowId: (row: T) => Key;
    mobileRow?: (row: T) => MobileRowDescriptor | ReactNode;
    inlineActions?: (row: T) => ReactNode;
    rowActions?: (row: T) => ReactNode;
    selectionMode?: "none" | "single" | "multiple";
    selectedKeys?: Selection;
    onSelectionChange?: (keys: Selection) => void;
    /** Whether ≥1 row is currently selected (drives implied editing mode). */
    hasSelection?: boolean;
    density?: DataTableDensity;
    emptyState?: ReactNode;
    isLoading?: boolean;
    "aria-label": string;
    className?: string;
};

export function DataTableMobileView<T extends object>({
    data,
    getRowId,
    mobileRow,
    inlineActions,
    rowActions,
    selectionMode = "none",
    selectedKeys,
    onSelectionChange,
    hasSelection = false,
    density = "default",
    emptyState,
    isLoading,
    "aria-label": ariaLabel,
    className,
}: DataTableMobileViewProps<T>) {
    // Build items for react-stately
    const items = data.map((row) => ({
        key: getRowId(row),
        row,
    }));

    const itemsByKey = useMemo(() => {
        const map = new Map<Key, T>();
        for (const item of items) {
            map.set(item.key, item.row);
        }
        return map;
    }, [items]);

    const state = useListState({
        items,
        children: (item) => <Item key={item.key}>{null}</Item>,
        selectionMode: selectionMode === "none" ? undefined : selectionMode,
        selectedKeys,
        onSelectionChange,
    });

    const listRef = useRef<HTMLUListElement | null>(null);
    const { gridProps } = useGridList({ "aria-label": ariaLabel }, state, listRef);

    const isEmpty = [...state.collection].length === 0;

    return (
        <div className={cn("relative w-full", isLoading && "opacity-50 transition-opacity", className)}>
            {isEmpty && emptyState ? (
                <div className="py-8 text-center">{emptyState}</div>
            ) : (
                <ul
                    {...gridProps}
                    ref={listRef}
                    className="divide-y divide-border-base overflow-hidden rounded-xl border border-border-base bg-surface-secondary shadow-sm"
                >
                    {[...state.collection].map((item) => {
                        const row = itemsByKey.get(item.key);
                        if (!row) return null;
                        return (
                            <MobileListItem
                                key={item.key}
                                item={item}
                                state={state}
                                row={row}
                                mobileRow={mobileRow}
                                inlineActions={inlineActions}
                                rowActions={rowActions}
                                density={density}
                                selectionMode={selectionMode}
                                hasSelection={hasSelection}
                            />
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/**
 * Strip pointer/mouse/press event handlers so row-level taps don't toggle
 * selection. Checkbox cells have their own independent event handling.
 */
function stripSelectionPointerEvents<T extends object>(props: T): T {
    const cleaned = { ...props };
    for (const key of Object.keys(cleaned)) {
        if (key.startsWith("onPointer") || key.startsWith("onMouse") || key === "onClick") {
            delete (cleaned as Record<string, unknown>)[key];
        }
    }
    return cleaned;
}

// ============================================================================
// MobileListItem
// ============================================================================

type MobileListItemProps<T> = {
    item: Node<unknown>;
    state: ReturnType<typeof useListState>;
    row: T;
    mobileRow?: (row: T) => MobileRowDescriptor | ReactNode;
    inlineActions?: (row: T) => ReactNode;
    rowActions?: (row: T) => ReactNode;
    density: DataTableDensity;
    selectionMode: "none" | "single" | "multiple";
    hasSelection: boolean;
};

function MobileListItem<T extends object>({
    item,
    state,
    row,
    mobileRow,
    inlineActions,
    rowActions,
    density,
    selectionMode,
    hasSelection,
}: MobileListItemProps<T>) {
    const ref = useRef<HTMLLIElement | null>(null);
    const { rowProps, gridCellProps, isPressed } = useGridListItem({ node: item }, state, ref);
    const { isFocusVisible, focusProps } = useFocusRing();

    const isSelected = state.selectionManager.isSelected(item.key);
    const showCheckbox = selectionMode === "multiple";

    // When no rows are selected, strip pointer events so tapping the row
    // doesn't toggle selection. Checkboxes still work via their own handlers.
    const effectiveRowProps = showCheckbox && !hasSelection ? stripSelectionPointerEvents(rowProps) : rowProps;

    // Get content from mobileRow or render a fallback
    const content = mobileRow ? mobileRow(row) : null;
    const descriptor = content && isMobileRowDescriptor(content) ? content : null;
    const rawContent = content && !isMobileRowDescriptor(content) ? content : null;

    return (
        <li
            ref={ref}
            {...mergeProps(effectiveRowProps, focusProps)}
            className={cn(
                "outline-none transition-colors duration-75",
                "first:rounded-t-xl last:rounded-b-xl",
                isSelected ? "bg-surface-brand/5" : "hover:bg-surface-button-clear-accent",
                isPressed && "bg-muted/10",
                isFocusVisible && "ring-1 ring-inset ring-border-brand",
                // Implied editing mode: pointer when selecting, auto (browser default) otherwise
                showCheckbox && hasSelection && "cursor-pointer",
                showCheckbox && !hasSelection && "cursor-auto",
            )}
        >
            <div
                {...gridCellProps}
                className={cn("flex items-center gap-3", density === "condensed" ? "px-3 py-2" : "px-4 py-3.5")}
            >
                {showCheckbox && <MobileSelectionCheckbox itemKey={item.key} state={state} />}

                <div className="min-w-0 flex-1">
                    {descriptor ? (
                        <MobileRowFromDescriptor descriptor={descriptor} />
                    ) : rawContent ? (
                        rawContent
                    ) : (
                        // Fallback: just show the row as JSON-ish (shouldn't happen with auto-gen)
                        <span className="text-base text-content-secondary">{String(item.key)}</span>
                    )}
                </div>

                {(inlineActions || rowActions) && (
                    <div
                        className="flex flex-shrink-0 items-center gap-1"
                        data-tracking-id-none
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                    >
                        {inlineActions?.(row)}
                        {rowActions && <DropdownActions contentClassName="w-48">{rowActions(row)}</DropdownActions>}
                    </div>
                )}
            </div>
        </li>
    );
}

// ============================================================================
// MobileRowFromDescriptor — renders a MobileRowDescriptor
// ============================================================================

function MobileRowFromDescriptor({ descriptor }: { descriptor: MobileRowDescriptor }) {
    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex min-w-0 items-center gap-2">
                {descriptor.icon && <div className="flex-shrink-0">{descriptor.icon}</div>}
                <div className="min-w-0 text-base font-medium text-content-primary">{descriptor.title}</div>
            </div>
            {descriptor.description && (
                <span className="text-base text-content-secondary">{descriptor.description}</span>
            )}
            {descriptor.details && descriptor.details.length > 0 && (
                <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    {descriptor.details.map((detail, i) => (
                        <span key={detail.label ?? i} className="text-base text-content-secondary">
                            {detail.label && <span className="text-content-tertiary">{detail.label}: </span>}
                            {detail.value}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// MobileSelectionCheckbox
// ============================================================================

function MobileSelectionCheckbox({ itemKey, state }: { itemKey: Key; state: ReturnType<typeof useListState> }) {
    const ref = useRef<HTMLInputElement | null>(null);
    const { checkboxProps } = useGridListSelectionCheckbox({ key: itemKey }, state);
    const toggleState = useToggleState(checkboxProps);
    const { inputProps } = useCheckbox(checkboxProps, toggleState, ref);

    return (
        <div className="flex flex-shrink-0 items-center">
            <input
                {...inputProps}
                ref={ref}
                className={cn(
                    // Larger hit target for coarse pointers (touch devices)
                    "size-4 rounded border pointer-coarse:size-5",
                    "cursor-pointer",
                    "accent-content-brand",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-brand focus-visible:ring-offset-1",
                )}
            />
        </div>
    );
}
